/**
 * Streaming's two pieces drawn to the redesign: the signing secret shown once
 * (§42.7) and the add row with several controls (§42.8).
 *
 * The secret is a note ok under the page's title — its title, the secret in a
 * copyable read-only field, the receiver's instructions with the headers in
 * mono, and « I copied it » at its right, which closes it for good: the Worker
 * keeps no way to show it again.
 *
 * The add row sits under the endpoints' table, on its hairline: the address
 * in mono, the format's native select, the Audit and Runs boxes, and the
 * secondary « Add endpoint » at their right. It is held at 40 % with its reason
 * in the tooltip while something is missing (« Choose Audit, Runs or both »,
 * 74f); what is wrong with the address, or the Worker's refusal, is said under
 * the field and the row grows; « Adding » while it runs.
 */
import { useState } from 'react';
import type React from 'react';
import {
  STREAM_HEADERS,
  STREAM_TOLERANCE_SECONDS,
  streamUrlProblem,
  type StreamFormat,
  type StreamKind
} from '@airlock/contracts/runs/streams';
import { color, font, radius } from '../../theme/tokens';
import { useTooltip } from '../../components/Tooltip';
import { CopyableKey } from '../identity/two-factor-parts';

/** What the add row asks the Worker for. */
export interface StreamRequest {
  url: string;
  format: StreamFormat;
  kinds: StreamKind[];
}

export const KIND_WORDS: Record<StreamKind, string> = { audit: 'Audit', runs: 'Runs' };

const CODE: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 12,
  padding: '1px 5px',
  borderRadius: 4,
  background: color.surface,
  color: color.ink
};

/** The signing secret, shown this once (§42.7, 75a). */
export function SecretShown({
  host,
  value,
  onDone
}: {
  host: string;
  value: string;
  onDone: () => void;
}): React.JSX.Element {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '12px 14px',
        borderRadius: radius.md,
        background: color.okBg,
        color: color.okText,
        fontSize: 13,
        lineHeight: 1.55
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{`The signing secret for ${host} — copy it now`}</div>
        <CopyableKey label={`The signing secret for ${host}`} value={value} />
        <div>
          It is shown this once. Give it to the receiver: each delivery carries{' '}
          <code style={CODE}>{STREAM_HEADERS.timestamp}</code> and{' '}
          <code style={CODE}>{STREAM_HEADERS.signature}</code> —{' '}
          <code style={CODE}>v1=</code> and the hex HMAC-SHA256 of{' '}
          <code style={CODE}>timestamp.body</code> under this secret. Refuse a timestamp more than{' '}
          {STREAM_TOLERANCE_SECONDS / 60} minutes away from your clock, and a replay fails.
        </div>
      </div>
      <button
        type="button"
        onClick={onDone}
        style={{
          flex: 'none',
          background: 'none',
          border: 0,
          padding: 0,
          fontFamily: 'inherit',
          fontSize: 13,
          color: color.okText,
          cursor: 'pointer'
        }}
      >
        I copied it
      </button>
    </div>
  );
}

/** What is wrong with a typed address, in the redesign's words where it has one (74e). */
function addressProblem(url: string): string | null {
  if (!/^https:\/\//i.test(url)) return 'Use an https:// address.';
  return streamUrlProblem(url);
}

/** The add row under the endpoints' table (§42.8). `onAdd` answers the refusal to show, or null once added. */
export function AddStream({
  unavailable,
  onAdd
}: {
  /** Why nothing can be added at all on this deployment, if so. */
  unavailable: string | null;
  onAdd: (request: StreamRequest) => Promise<string | null>;
}): React.JSX.Element {
  const [url, setUrl] = useState('');
  const [format, setFormat] = useState<StreamFormat>('json');
  const [kinds, setKinds] = useState<StreamKind[]>(['audit', 'runs']);
  const [running, setRunning] = useState(false);
  // An address is judged once it is left or sent, not at every keystroke.
  const [judged, setJudged] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const problem = url ? addressProblem(url) : null;
  const shownError = refusal ?? (judged ? problem : null);
  const missing = !url ? 'Type an address first' : kinds.length === 0 ? 'Choose Audit, Runs or both' : undefined;
  const held = unavailable ?? problem ?? missing;
  const button = useTooltip<HTMLButtonElement>(running ? undefined : held, 'start');

  const add = async (): Promise<void> => {
    setJudged(true);
    if (held || running) return;
    setRunning(true);
    setRefusal(null);
    const said = await onAdd({ url, format, kinds });
    setRunning(false);
    setRefusal(said);
    if (!said) {
      setUrl('');
      setJudged(false);
    }
  };

  const control: React.CSSProperties = {
    height: 32,
    padding: '0 11px',
    border: `1px solid ${color.lineInput}`,
    borderRadius: 8,
    background: color.surface,
    fontFamily: 'inherit',
    fontSize: 13,
    color: color.ink
  };

  return (
    <form
      aria-label="Add an endpoint"
      onSubmit={(event) => {
        event.preventDefault();
        void add();
      }}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        flexWrap: 'wrap',
        padding: '12px 0',
        borderTop: `1px solid ${color.line}`
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 340, maxWidth: '100%' }}>
        <input
          aria-label="Endpoint address"
          placeholder="https://siem.example.com/airlock"
          value={url}
          aria-invalid={shownError ? true : undefined}
          onChange={(event) => {
            setUrl(event.target.value.trim());
            setRefusal(null);
          }}
          onBlur={() => setJudged(Boolean(url))}
          style={{
            ...control,
            width: '100%',
            fontFamily: font.mono,
            fontSize: 12.5,
            borderColor: shownError ? color.red : color.lineInput
          }}
        />
        {shownError && (
          <span role="alert" style={{ fontSize: 12.5, lineHeight: 1.45, color: color.badText }}>
            {shownError}
          </span>
        )}
      </div>
      <select
        aria-label="Format"
        value={format}
        onChange={(event) => setFormat(event.target.value as StreamFormat)}
        style={{ ...control, cursor: 'pointer' }}
      >
        <option value="json">JSON</option>
        <option value="slack">Slack</option>
      </select>
      {(['audit', 'runs'] as const).map((kind) => (
        <label
          key={kind}
          style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32, fontSize: 13, color: color.inkBody }}
        >
          <input
            type="checkbox"
            checked={kinds.includes(kind)}
            onChange={(event) =>
              setKinds((current) => (event.target.checked ? [...current, kind] : current.filter((one) => one !== kind)))
            }
          />
          {KIND_WORDS[kind]}
        </label>
      ))}
      <button
        type="submit"
        aria-disabled={held || running ? true : undefined}
        {...button.trigger}
        data-press=""
        className="hdr-btn"
        style={{
          flex: 'none',
          height: 32,
          padding: '0 12px',
          border: `1px solid ${color.line}`,
          borderRadius: radius.chip,
          background: 'transparent',
          fontFamily: 'inherit',
          fontSize: 13,
          color: color.inkBody,
          cursor: held || running ? 'default' : 'pointer',
          opacity: held ? 0.4 : 1
        }}
      >
        {running ? 'Adding' : 'Add endpoint'}
      </button>
      {button.tip}
    </form>
  );
}
